import { ClassValue } from 'clsx';
import { useId } from 'react';
import { LinkActionButtons } from '@shared/components/LinkActionButtons';
import { Menu } from '@shared/components/Menu';
import { Text } from '@shared/components/Text';
import { useToast } from '@shared/components/Toast';
import { View } from '@shared/components/View';
import { Link } from '@shared/components/link';
import { TruncateLink } from '@shared/components/truncate';
import { UrlField } from '@shared/data/entity/fields/UrlField';
import { getAnchorName } from '@shared/styles';
import { copyToClipboard } from '@shared/util/clipboard';
import { useIsDesktop } from '@shared/util/hooks';
import { i18n } from '@shared/util/i18n';
import { openLink } from '@shared/util/link';
import { addPrefixIfNeeded } from '@shared/util/validate/url';
import { CollectionViewFieldContainer } from './CollectionViewFieldContainer';
import { FieldVariant } from './FieldVariant';
import { useLongPressMenu } from './useLongPressMenu';
import { getUrlText } from './util/getUrlText';
import styles from './UrlCollectionViewField.module.css';

interface Props {
  variant: FieldVariant;
  className?: ClassValue;
  field: UrlField;
}

/**
 * Default and compact: the first link, truncating, and a "+N" for any more; on desktop a copy
 * button sits beside it, and on a touch device a long press opens a menu to open or copy it.
 * Inline: its text.
 */
export function UrlCollectionViewField({ field, variant, className }: Props) {
  if (field.isEmpty) {
    return null;
  }

  const isInline = variant === 'inline';
  const isCompact = variant === 'compact';
  const isDefault = variant === 'default';

  return (
    <CollectionViewFieldContainer
      fieldType={field.fieldType}
      variant={variant}
      className={[styles.urlCollectionViewField, className]}
    >
      {isInline && <InlineUrl field={field} />}
      {isCompact && <DefaultUrl field={field} />}
      {isDefault && <DefaultUrl field={field} />}
    </CollectionViewFieldContainer>
  );
}

function InlineUrl({ field }: { field: UrlField }) {
  return <>{getUrlText(field)}</>;
}

function DefaultUrl({ field }: { field: UrlField }) {
  const isDesktop = useIsDesktop();
  const { isMenuOpen, onMenuOpenChange, pressProps } = useLongPressMenu(!isDesktop);
  const anchorName = getAnchorName('url-collection-view-field', useId());
  const toast = useToast();
  const href = addPrefixIfNeeded(field.url);
  const moreCount = field.items.length - 1;
  const copyLink = () => {
    void copyToClipboard(href);
    toast({
      message: i18n('workflowTemplate.component.url.actionButtonsSheet.copiedToClipboard'),
    });
  };

  return (
    <View
      row
      centerVertically
      {...pressProps}
      data-long-press={!isDesktop || undefined}
      style={{ anchorName }}
      className={styles.row}
    >
      {/* On the View because Link doesn't pass extra props to its anchor. */}
      <View centerVertically className={styles.link} tooltip={isDesktop && field.url}>
        <Link href={href}>
          <TruncateLink href={href} />
        </Link>
      </View>
      {moreCount > 0 && <Text className={styles.more}>{`+${moreCount}`}</Text>}
      {isDesktop && (
        <View paddingLeft={1}>
          <LinkActionButtons value={href} type="url" showOpen={false} />
        </View>
      )}
      {/* Touch only: a long press opens it, in place of the desktop copy button. */}
      {!isDesktop && (
        <Menu
          isOpen={isMenuOpen}
          onOpenChange={onMenuOpenChange}
          anchorName={anchorName}
          // Its end lines up with the link's, which ends at the row's edge in a trailing slot.
          align="left"
        >
          <Menu.Content>
            <Menu.Note icon={false}>{field.url}</Menu.Note>
            <Menu.Item
              label={i18n('workflowTemplate.component.url.actionButtonsSheet.openLink')}
              onClick={() => openLink(href)}
            />
            <Menu.Item
              label={i18n('workflowTemplate.component.url.actionButtonsSheet.copyLink')}
              onClick={copyLink}
            />
          </Menu.Content>
        </Menu>
      )}
    </View>
  );
}
